import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from "react"
import { cn } from "@/lib/utils"

export type FloatingCardPos = { left: number; top: number }

export type FloatingCardEdgePads = {
  top?: number
  right?: number
  bottom?: number
  left?: number
}

const EDGE_PAD_PX = 8

function resolvePads(edgePad: number | FloatingCardEdgePads = EDGE_PAD_PX): {
  top: number
  right: number
  bottom: number
  left: number
} {
  if (typeof edgePad === "number") {
    return { top: edgePad, right: edgePad, bottom: edgePad, left: edgePad }
  }
  return {
    top: edgePad.top ?? EDGE_PAD_PX,
    right: edgePad.right ?? EDGE_PAD_PX,
    bottom: edgePad.bottom ?? EDGE_PAD_PX,
    left: edgePad.left ?? EDGE_PAD_PX,
  }
}

export function clampFloatingCardPos(
  left: number,
  top: number,
  parentW: number,
  parentH: number,
  cardW: number,
  cardH: number,
  edgePad: number | FloatingCardEdgePads = EDGE_PAD_PX,
): FloatingCardPos {
  const pads = resolvePads(edgePad)
  const maxL = Math.max(pads.left, parentW - cardW - pads.right)
  const maxT = Math.max(pads.top, parentH - cardH - pads.bottom)
  return {
    left: Math.min(Math.max(pads.left, left), maxL),
    top: Math.min(Math.max(pads.top, top), maxT),
  }
}

export function snapshotFloatingCardPos(panel: HTMLElement, parent: HTMLElement): FloatingCardPos {
  const parentRect = parent.getBoundingClientRect()
  const panelRect = panel.getBoundingClientRect()
  return {
    left: panelRect.left - parentRect.left,
    top: panelRect.top - parentRect.top,
  }
}

export function floatingCardParent(panel: HTMLElement | null): HTMLElement | null {
  if (!panel) return null
  return panel.offsetParent instanceof HTMLElement ? panel.offsetParent : panel.parentElement
}

export type FloatingDraggableCardHandle = {
  panel: () => HTMLDivElement | null
  getPos: () => FloatingCardPos | null
  setPos: (next: FloatingCardPos | null) => void
}

export type FloatingDraggableCardProps = {
  className?: string
  style?: CSSProperties
  /** Clears custom drag position when this changes. */
  resetKey?: string | number
  /**
   * Live default while the user has not dragged (e.g. track a 3D anchor).
   * Ignored after drag until resetKey.
   */
  defaultPos?: FloatingCardPos | null
  /** Used when dragged pos and defaultPos are both null (e.g. Sketch centered bottom). */
  unsetStyle?: CSSProperties
  /** Per-edge inset from offset parent; number applies to all sides. */
  edgePads?: number | FloatingCardEdgePads
  "aria-label"?: string
  header: ReactNode
  headerEnd?: ReactNode
  children: ReactNode
  cardRef?: Ref<FloatingDraggableCardHandle | null>
  onPosChange?: (pos: FloatingCardPos | null) => void
}

/**
 * Absolute floating card with header drag + clamp to offset parent.
 * Position model: `left` / `top` in parent CSS px.
 */
export function FloatingDraggableCard({
  className,
  style,
  resetKey = 0,
  defaultPos = null,
  unsetStyle,
  edgePads = EDGE_PAD_PX,
  "aria-label": ariaLabel,
  header,
  headerEnd,
  children,
  cardRef,
  onPosChange,
}: FloatingDraggableCardProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const posRef = useRef<FloatingCardPos | null>(null)
  const defaultPosRef = useRef(defaultPos)
  const edgePadsRef = useRef(edgePads)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    origLeft: number
    origTop: number
  } | null>(null)
  const [pos, setPosState] = useState<FloatingCardPos | null>(null)
  const [clampedDefault, setClampedDefault] = useState<FloatingCardPos | null>(null)
  const [dragging, setDragging] = useState(false)
  posRef.current = pos
  defaultPosRef.current = defaultPos
  edgePadsRef.current = edgePads

  const setPos = (next: FloatingCardPos | null) => {
    posRef.current = next
    setPosState(next)
    onPosChange?.(next)
  }

  useImperativeHandle(
    cardRef,
    () => ({
      panel: () => panelRef.current,
      getPos: () => posRef.current,
      setPos,
    }),
    // setPos identity changes; handle always reads latest via refs/state setters
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable handle API
    [onPosChange],
  )

  useLayoutEffect(() => {
    setPos(null)
    setClampedDefault(null)
    setDragging(false)
    dragRef.current = null
    document.body.style.removeProperty("cursor")
    document.body.style.removeProperty("user-select")
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on resetKey
  }, [resetKey])

  useEffect(() => {
    return () => {
      document.body.style.removeProperty("cursor")
      document.body.style.removeProperty("user-select")
    }
  }, [])

  useLayoutEffect(() => {
    const panel = panelRef.current
    const parent = floatingCardParent(panel)
    if (!panel || !parent) return

    const reclamp = () => {
      const pads = edgePadsRef.current
      const dragged = posRef.current
      if (dragged) {
        const next = clampFloatingCardPos(
          dragged.left,
          dragged.top,
          parent.clientWidth,
          parent.clientHeight,
          panel.offsetWidth,
          panel.offsetHeight,
          pads,
        )
        if (next.left !== dragged.left || next.top !== dragged.top) setPos(next)
        return
      }
      const def = defaultPosRef.current
      if (!def) {
        setClampedDefault(null)
        return
      }
      const next = clampFloatingCardPos(
        def.left,
        def.top,
        parent.clientWidth,
        parent.clientHeight,
        panel.offsetWidth,
        panel.offsetHeight,
        pads,
      )
      setClampedDefault((prev) => (prev && prev.left === next.left && prev.top === next.top ? prev : next))
    }

    reclamp()
    const ro = new ResizeObserver(reclamp)
    ro.observe(parent)
    ro.observe(panel)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- observe for layout; setPos stable enough
  }, [resetKey, defaultPos, edgePads])

  const onHeaderPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    const target = event.target
    if (target instanceof Element && target.closest("button, input, textarea, a")) return
    const panel = panelRef.current
    const parent = floatingCardParent(panel)
    if (!panel || !parent) return

    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)

    const snapped = snapshotFloatingCardPos(panel, parent)
    const next = clampFloatingCardPos(
      snapped.left,
      snapped.top,
      parent.clientWidth,
      parent.clientHeight,
      panel.offsetWidth,
      panel.offsetHeight,
      edgePadsRef.current,
    )
    setPos(next)
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origLeft: snapped.left,
      origTop: snapped.top,
    }
  }

  const onHeaderPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    const panel = panelRef.current
    const parent = floatingCardParent(panel)
    if (!drag || drag.pointerId !== event.pointerId || !panel || !parent) return
    setPos(
      clampFloatingCardPos(
        drag.origLeft + (event.clientX - drag.startX),
        drag.origTop + (event.clientY - drag.startY),
        parent.clientWidth,
        parent.clientHeight,
        panel.offsetWidth,
        panel.offsetHeight,
        edgePadsRef.current,
      ),
    )
  }

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const effective = pos ?? clampedDefault ?? defaultPos
  const positionStyle: CSSProperties | undefined = effective
    ? { left: effective.left, top: effective.top, right: "auto", bottom: "auto", transform: "none" }
    : unsetStyle

  return (
    <div
      ref={panelRef}
      className={cn("absolute", className)}
      aria-label={ariaLabel}
      style={{ ...positionStyle, ...style }}
    >
      <div className="flex w-full shrink-0 items-center gap-2">
        <div
          className={cn(
            "flex shrink-0 touch-none items-center gap-2 select-none",
            dragging ? "cursor-grabbing" : "cursor-grab",
          )}
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {header}
        </div>
        {headerEnd}
      </div>
      {children}
    </div>
  )
}
